import { instanceEmployee, instanceTimekeeper } from '../../../../services/instances';
import type {
    DocumentType,
    Employee,
    EmployeeAccrualProfile,
    EmployeePayRule,
    CreateEmployeeAccrualProfiles,
    GetEmployees,
    GetEmployeeById,
    GetEmployeeAccrualProfiles,
    GetEmployeePayRules,
    PatchEmployee,
    PayRule,
    PostEmployee,
    Alert,
    SetEmployeePayRules,
    UpdateEmployeeAccrualProfiles,
    ResponseOrgpathEmployee,
    TKResponse,
} from '../types/services';
import type { SelectOption } from '../types/hooks';

const getEmployees = async ({ params, alert }: GetEmployees): Promise<{ employees: Employee[] }> => {
    const { showAlert, t } = alert;

    let employees: Employee[] = [];

    await instanceEmployee
        .get('/employee', { params })
        .then((response) => {
            employees = response.data.data;
        })
        .catch(() => {
            showAlert('error', t('alerts.error_get_employees'));
        });

    return {
        employees,
    };
};

const getDocumentTypes = async () => {
    let documentTypes: DocumentType[] = [];

    await instanceEmployee
        .get('document_type')
        .then((response) => {
            documentTypes = response.data.data;

            documentTypes = documentTypes.map((docType: any) => ({
                value: docType.id,
                label: docType.name,
                sublabel: '',
                regex: docType.regex,
            }));
        })
        .catch((error) => console.error(error.message));

    return {
        documentTypes,
    };
};

const getPayrules = async () => {
    let payrules: PayRule[] = [];

    await instanceTimekeeper
        .get('/pay_rules')
        .then((response: any) => {
            const pay_rules: PayRule[] = response.data.data;

            payrules = pay_rules.map((rule: any) => ({
                value: rule.pay_rule_id,
                label: rule.name,
                sublabel: rule.description,
            }));
        })
        .catch((error) => {
            console.error(error.message);
        });

    return { payrules };
};

const getEmployeePayRules = async ({ employeeId, alert }: GetEmployeePayRules): Promise<{ payRules: EmployeePayRule[] }> => {
    const { showAlert, t } = alert;
    let payRules: EmployeePayRule[] = [];

    await instanceEmployee
        .get('/employee_r_pay_rule', { params: { employee_id: Number(employeeId) } })
        .then((response) => {
            payRules = response.data.data || [];
        })
        .catch((error) => {
            console.error(error.message);
            showAlert('error', t('assign_pay_rule.alerts.load_error'));
        });

    return { payRules };
};

const setEmployeePayRules = async ({ employeeId, body, alert }: SetEmployeePayRules): Promise<boolean> => {
    const { showAlert, t } = alert;

    try {
        await instanceEmployee.patch(`/employee/${employeeId}/pay_rule`, body);
        return true;
    } catch (error: any) {
        console.error(error.message);
        showAlert('error', t('assign_pay_rule.alerts.error'));
        return false;
    }
};

const getEmployeeById = async ({ employeeId }: GetEmployeeById): Promise<{ employee: Employee | null }> => {
    let employee: Employee | null = null;

    await instanceEmployee
        .get(`/employee/${employeeId}`)
        .then((response) => {
            employee = response.data.data;
        })
        .catch((error) => console.error(error.message));

    return { employee };
};

const addEmployee = async ({ params: { setLoading, body, closing, reloading }, alert: { showAlert, closeAlert, t } }: PostEmployee) => {
    const key = showAlert('default', t('add_worker.alerts.save_inprogress.message'));

    setLoading(true);

    instanceEmployee
        .post('/employee/create', body)
        .then(() => {
            closeAlert?.(key);
            showAlert('success', t('add_worker.alerts.save_success.message'));
        })
        .catch((error) => {
            console.error(error.message);
            closeAlert?.(key);
            showAlert('error', t('add_worker.alerts.save_error.message'));
        })
        .finally(() => {
            closing();
            reloading();
            setLoading(false);
        });
};

const updateEmployee = async ({ params: { setLoading, employeeId, body, closing, reloading }, alert: { showAlert, closeAlert, t } }: PatchEmployee) => {
    const key = showAlert('default', t('edit_worker.alerts.save_inprogress.message'));

    setLoading(true);

    instanceEmployee
        .patch(`/employee/${employeeId}`, body)
        .then(() => {
            closeAlert?.(String(key));
            showAlert('success', t('edit_worker.alerts.save_success.message'));

            closing();
            reloading();
        })
        .catch((error) => {
            console.error(error.message);
            showAlert('error', t('edit_worker.alerts.save_error.message'));
        })
        .finally(() => {
            setLoading(false);
        });
};

const getAccrualProfiles = async ({ alert }: { alert: Alert }) => {
    const { showAlert, t } = alert;
    let profiles: SelectOption[] = [];

    await instanceTimekeeper
        .get('/accruals/accrual_profiles')
        .then((response) => {
            profiles = response.data.data
                .map((profile: any) => ({
                    value: String(profile.accrual_profile_id),
                    label: profile.name || profile.description || `Perfil ${profile.accrual_profile_id}`,
                    sublabel: profile.description || '',
                }))
                .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label));
        })
        .catch((error) => {
            console.error(error.message);
            showAlert('error', t('assign_accrual_profile.alerts.load_error'));
        });

    return { profiles };
};

const getEmployeeAccrualProfiles = async ({ employeeId, alert }: GetEmployeeAccrualProfiles): Promise<{ profiles: EmployeeAccrualProfile[] }> => {
    const { showAlert, t } = alert;
    let profiles: EmployeeAccrualProfile[] = [];

    await instanceTimekeeper
        .get(`/accruals/accrual_profiles_employee?employee_id=${employeeId}`)
        .then((response) => {
            profiles = response.data.data || [];
        })
        .catch((error) => {
            console.error(error.message);
            showAlert('error', t('assign_accrual_profile.alerts.load_error'));
        });

    return { profiles };
};

const createEmployeeAccrualProfiles = async ({ body, alert }: CreateEmployeeAccrualProfiles): Promise<boolean> => {
    const { showAlert, t } = alert;

    try {
        await instanceTimekeeper.post('/accruals/accrual_profiles_employee', body);
        return true;
    } catch (error: any) {
        console.error(error.message);
        showAlert('error', t('assign_accrual_profile.alerts.error'));
        return false;
    }
};

const updateEmployeeAccrualProfiles = async ({ employeeId, body, alert }: UpdateEmployeeAccrualProfiles): Promise<boolean> => {
    const { showAlert, t } = alert;

    try {
        await instanceTimekeeper.patch(`/accruals/accrual_profiles_employee/${employeeId}`, body);
        return true;
    } catch (error: any) {
        console.error(error.message);
        showAlert('error', t('assign_accrual_profile.alerts.error'));
        return false;
    }
};

const getOrgpathByEmployeeId = async ({ employeeId }: { employeeId: number }): Promise<ResponseOrgpathEmployee> => {
    const response = await instanceEmployee.get<TKResponse<ResponseOrgpathEmployee>>(`/employee/${employeeId}/orgpath`);
    return response.data.data;
};

export {
    getEmployees,
    getDocumentTypes,
    getPayrules,
    getEmployeePayRules,
    setEmployeePayRules,
    getEmployeeById,
    addEmployee,
    updateEmployee,
    getAccrualProfiles,
    getEmployeeAccrualProfiles,
    createEmployeeAccrualProfiles,
    updateEmployeeAccrualProfiles,
    getOrgpathByEmployeeId,
};
